'use client';

import { useEffect, useRef, useState } from 'react';

interface PrivacyPolicyModalProps {
  open: boolean;
  onClose: () => void;
  onAgree: () => void;
}

export default function PrivacyPolicyModal({ open, onClose, onAgree }: PrivacyPolicyModalProps) {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  useEffect(() => {
    if (!open) return;
    const el = contentRef.current;
    if (!el) return;

    const onScroll = () => {
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
      if (atBottom) setScrolledToBottom(true);
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="privacy-title"
        className="relative bg-white rounded-lg shadow-lg w-full max-w-2xl mx-4"
      >
        {/* Close */}
        <button
          aria-label="閉じる"
          onClick={onClose}
          className="absolute right-3 top-3 text-gray-500 hover:text-gray-700"
        >
          ×
        </button>

        <div className="p-6">
          <h2 id="privacy-title" className="text-xl font-semibold mb-3">プライバシーポリシー</h2>
          <p className="text-sm text-gray-700 mb-3">
            本サービスは、ユーザーの安心と信頼を最優先に、入力内容の収集・活用を行わない方針で運営しています。以下、プライバシー保護に関する詳細をご確認ください。
          </p>
          <div
            ref={contentRef}
            className="border rounded-md p-4 h-48 overflow-y-auto text-sm text-gray-700"
          >
            <p className="mb-2"><strong>1. 目的</strong>：本サービスは、東京都の税務に関する情報提供・相談の補助を目的としており、ユーザーが安心して対話できる体験の提供に努めます。</p>
            <p className="mb-2"><strong>2. 収集方針</strong>：ユーザーがチャットに入力したテキスト等の内容を、分析・学習・二次利用のために収集・保存・活用することは一切ありません。サービス改善やマーケティング目的での利用も行いません。</p>
            <p className="mb-2"><strong>3. ログデータ</strong>：システムの安定運用および不正防止のため、アクセス時刻・エラーログなど最小限の技術的ログが生成される場合があります。これらは法令および適用されるガイドラインに従い、必要な範囲でのみ取り扱います。</p>
            <p className="mb-2"><strong>4. 第三者提供</strong>：法令に基づく場合を除き、第三者への提供は行いません。司法・行政機関からの適法な要請がある場合に限って、必要最小限の範囲で対応します。</p>
            <p className="mb-2"><strong>5. セキュリティ</strong>：適切な技術的・組織的安全管理措置を講じ、システムの保護に努めます。ログデータ等はアクセス制御・暗号化等の手段によって安全性を確保します。</p>
            <p className="mb-2"><strong>6. クッキー等の利用</strong>：ユーザー体験向上のため、同意状態や表示設定等をブラウザのローカルストレージまたはクッキーに保存することがあります。これらは機能提供のための最小限の利用に限定します。</p>
            <p className="mb-2"><strong>7. 保管期間・削除</strong>：法令で保管が定められる技術的ログを除き、個人情報に該当しうるデータの保管は行いません。必要なログは所定の期間経過後、適切な方法で破棄します。</p>
            <p className="mb-2"><strong>8. ユーザーの権利</strong>：お問い合わせに応じて、運用に関する説明、必要に応じた対応を行います。記録されるログの範囲等については、透明性を確保します。</p>
            <p className="mb-2"><strong>9. ポリシーの改定</strong>：法令やサービス内容の変更に伴い、ポリシーを改定することがあります。重要な変更時には、わかりやすい方法でお知らせします。</p>
            <p className="mb-2"><strong>10. 免責・注意事項</strong>：本サービスの回答は参考情報であり、最終的な判断は税務関係者・専門家へ相談の上で行ってください。</p>
            <p className="mb-2"><strong>11. お問い合わせ</strong>：プライバシーに関するご質問は、運営窓口までご連絡ください。対応にあたり、本人確認等をお願いする場合があります。</p>
            <p className="mb-2"><strong>施行日</strong>：このプライバシーポリシーは公表日より適用されます。</p>
          </div>

          <div className="mt-4 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              キャンセル
            </button>
            <button
              onClick={onAgree}
              disabled={!scrolledToBottom}
              className={`${scrolledToBottom ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-300'} px-4 py-2 rounded-md text-white`}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
